import { useCallback, useMemo } from "react";
import type { CatalogPrice } from "@ferrivia/api-contract";
import { ApiError } from "@ferrivia/api-contract";
import { useApi, useSession, useTracker } from "@ferrivia/auth-web";
import { PENDING_CHECKOUT_ITEMS_KEY } from "../../hooks/useOrderConfirmation";

/** Starts a Stripe Checkout for the given lookup keys and redirects to it.
    Rejects on failure so the caller can show its own error; the store.*
    event names are kept from the Store tab this came from, so the series
    stays continuous. */
export function useCheckout(priceBySlug: Map<string, CatalogPrice>): (lookupKeys: string[]) => Promise<void> {
  const api = useApi();
  const { trust, user } = useSession();
  const tracker = useTracker();

  // Inverse of priceBySlug: the stash for the confirmation dialog is keyed by
  // slug, since product copy is (see productCopy.ts).
  const slugByLookupKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const [slug, price] of priceBySlug) {
      map.set(price.lookupKey, slug);
    }
    return map;
  }, [priceBySlug]);

  return useCallback(
    async (lookupKeys: string[]) => {
      if (trust !== "verified") return;
      tracker.track("store.checkout_started", {
        lookup_key: lookupKeys[0],
        addon_included: lookupKeys.length > 1,
        item_count: lookupKeys.length,
      });
      try {
        const session = await api.commerce.createCheckoutSession({
          items: lookupKeys.map((lookupKey) => ({ lookupKey, quantity: 1 })),
          email: user?.email ?? undefined,
          source: "client_app",
          analyticsSessionId: tracker.sessionId,
        });
        const slugs = lookupKeys
          .map((lookupKey) => slugByLookupKey.get(lookupKey))
          .filter((slug): slug is string => Boolean(slug));
        sessionStorage.setItem(PENDING_CHECKOUT_ITEMS_KEY, JSON.stringify(slugs));
        window.location.href = session.checkoutUrl;
      } catch (err) {
        tracker.track("store.checkout_failed", { reason: err instanceof ApiError ? err.code : "network" });
        throw err;
      }
    },
    [api, trust, tracker, user, slugByLookupKey],
  );
}
